import { storage } from '../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

/**
 * Image Upload Service (Firebase Storage)
 * Uploads images to Firebase Storage and returns the download URL.
 */

/**
 * Upload a single image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} folder - The folder in storage (default: 'blog-images')
 * @returns {Promise<string>} - The HTTPS download URL of the uploaded image
 */
export const uploadImage = async (file, folder = 'blog-images') => {
    if (!file) {
        throw new Error('No file provided');
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
    }

    // Validate file size (max 15MB)
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
        throw new Error('File size must be less than 15MB');
    }

    try {
        // Create a unique filename
        const fileExtension = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExtension}`;
        const storageRef = ref(storage, `${folder}/${fileName}`);

        // Upload the file
        const snapshot = await uploadBytes(storageRef, file);

        // Get the download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        return downloadURL;
    } catch (error) {
        console.error('Error uploading image to Firebase:', error);
        throw new Error(`Upload failed: ${error.message}`);
    }
};

/**
 * Upload multiple image files to Firebase Storage
 * @param {FileList|File[]} files - Array of image files to upload
 * @param {string} folder - The folder in storage (default: 'blog-images')
 * @returns {Promise<string[]>} - Array of HTTPS download URLs
 */
export const uploadMultipleImages = async (files, folder = 'blog-images') => {
    if (!files || files.length === 0) {
        throw new Error('No files provided');
    }

    const uploadPromises = Array.from(files).map(file => uploadImage(file, folder));

    try {

        // Note: In the previous implementation we used Promise.all(uploadPromises)
        // But since uploadImage is async, mapping it returns an array of promises.
        // We need to await Promise.all on that array.
        // Wait, uploadPromises IS an array of promises.
        const resolvedUrls = await Promise.all(uploadPromises);
        return resolvedUrls;
    } catch (error) {
        console.error('Error uploading multiple images:', error);
        throw error;
    }
};
